import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    取得優先處理問題,
    取得引用結果,
    取得後續補強問題,
    取得處理優先度,
} from "@/lib/aiCitationOperations";
import { CheckCircle2, Link2, Target } from "lucide-react";

interface ResultsDashboardProps {
    results: any;
    onAddTrackingKeyword: (
        keyword: string,
        category?: string | null,
        primaryIntent?: "informational" | "commercial" | "transactional"
    ) => Promise<void>;
    isAddingTracking?: boolean;
}

export default function ResultsDashboard({ results, onAddTrackingKeyword, isAddingTracking }: ResultsDashboardProps) {
    const trackedKeywordCount = new Set(
        results.intentCoverage.trackedMatches.map((match: any) => String(match.intentKeyword))
    ).size;
    const addableKeywordCount = new Set(
        results.intentCoverage.untrackedIntents.map((intent: any) => String(intent.intentKeyword))
    ).size;
    const 引用結果 = 取得引用結果(results);
    const 處理優先度 = 取得處理優先度(results);
    const 優先處理問題 = 取得優先處理問題(results);
    const 後續補強問題 = 取得後續補強問題(results);

    return (
        <div className="space-y-6">
            <Card className="border-2 border-primary/10">
                <CardHeader className="pb-3">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <CardTitle className="text-lg">文章引用規劃摘要</CardTitle>
                            <p className="text-sm text-muted-foreground mt-1">{results.summary.headline}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Badge className={引用結果 === "已有引用" ? "bg-sky-700" : ""} variant={引用結果 === "已有引用" ? "default" : "secondary"}>
                                {引用結果}
                            </Badge>
                            <Badge
                                variant={處理優先度 === "優先處理" ? "destructive" : 處理優先度 === "後續補強" ? "secondary" : "outline"}
                                className={處理優先度 === "後續補強" ? "text-amber-700" : 處理優先度 === "目前穩定" ? "text-green-700 border-green-200" : ""}
                            >
                                {處理優先度}
                            </Badge>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <div className="rounded-lg border bg-background p-4">
                        <div className="text-sm text-muted-foreground">頁面類型</div>
                        <div className="mt-2 text-2xl font-semibold">{results.pageClassification || "內容頁"}</div>
                    </div>
                    <div className="rounded-lg border bg-background p-4">
                        <div className="text-sm text-muted-foreground">潛在關鍵字</div>
                        <div className="mt-2 text-2xl font-semibold">{results.intentCoverage.inferredIntents.length}</div>
                    </div>
                    <div className="rounded-lg border bg-background p-4">
                        <div className="text-sm text-muted-foreground">已追蹤</div>
                        <div className="mt-2 text-2xl font-semibold">{trackedKeywordCount}</div>
                    </div>
                    <div className="rounded-lg border bg-background p-4">
                        <div className="text-sm text-muted-foreground">未追蹤</div>
                        <div className="mt-2 text-2xl font-semibold">{addableKeywordCount}</div>
                    </div>
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Target className="h-4 w-4" />
                            潛在意圖
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {results.intentCoverage.inferredIntents.length === 0 ? (
                            <div className="text-sm text-muted-foreground">目前無法從這篇內容穩定推導潛在關鍵字。</div>
                        ) : (
                            results.intentCoverage.inferredIntents.map((intent: any) => {
                                const trackedMatch = results.intentCoverage.trackedMatches.find(
                                    (match: any) => match.intentKeyword === intent.intentKeyword
                                );
                                const untrackedIntent = results.intentCoverage.untrackedIntents.find(
                                    (item: any) => item.intentKeyword === intent.intentKeyword
                                );

                                return (
                                    <div
                                        key={intent.intentKeyword}
                                        className="rounded-lg border p-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                                    >
                                        <div className="min-w-0">
                                            <div className="font-medium break-words">{intent.intentKeyword}</div>
                                            {trackedMatch && trackedMatch.matchedKeyword !== intent.intentKeyword ? (
                                                <div className="text-xs text-muted-foreground mt-1">
                                                    對應關鍵字：{trackedMatch.matchedKeyword}
                                                </div>
                                            ) : null}
                                        </div>
                                        {trackedMatch ? (
                                            <Badge className="bg-green-600">已追蹤</Badge>
                                        ) : untrackedIntent ? (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                disabled={isAddingTracking}
                                                onClick={() => onAddTrackingKeyword(intent.intentKeyword, untrackedIntent.suggestedCategory, untrackedIntent.suggestedPrimaryIntent)}
                                            >
                                                <Link2 className="h-3.5 w-3.5 mr-1" />
                                                加入關鍵字列表
                                            </Button>
                                        ) : (
                                            <Badge variant="secondary">待確認</Badge>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </CardContent>
                </Card>

                <Card className="border-blue-200 bg-blue-50/40">
                    <CardHeader>
                        <CardTitle>引用結果觀察</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                            <div className="rounded-lg bg-white border p-3">
                                <div className="text-muted-foreground">優先處理問題</div>
                                <div className="text-2xl font-semibold mt-1">{優先處理問題.length}</div>
                            </div>
                            <div className="rounded-lg bg-white border p-3">
                                <div className="text-muted-foreground">後續補強問題</div>
                                <div className="text-2xl font-semibold mt-1">{後續補強問題.length}</div>
                            </div>
                            <div className="rounded-lg bg-white border p-3">
                                <div className="text-muted-foreground">引用來源</div>
                                <div className="mt-2 font-semibold">
                                    {results.intentCoverage.pageEvidence?.providers?.length
                                        ? results.intentCoverage.pageEvidence.providers.join("、")
                                        : "無"}
                                </div>
                            </div>
                        </div>

                        {results.intentCoverage.pageEvidence?.matchedCitationKeywords?.length ? (
                            <div className="text-sm text-muted-foreground">
                                近 30 天命中的查詢：{results.intentCoverage.pageEvidence.matchedCitationKeywords.join("、")}
                            </div>
                        ) : (
                            <div className="text-sm flex items-start gap-2 text-muted-foreground">
                                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                                <span>目前還沒有比對到這一頁近 30 天命中的查詢。</span>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
